export interface ILateFeeDay {
  day: number;
  rate: number;
  dailyFee: number;
}

export interface ILateFeePeriod {
  /** Inclusive range, MM-DD. */
  from: string;
  to: string;
  days: number;
  /** Percent; 0 means "no rate" (grace, flat fee). */
  rate: number;
  fee: number;
  kind: "grace" | "charged" | "capped";
}

const MS_DAY = 86_400_000;

/** YYYY-MM-DD + n calendar days -> MM-DD (plain UTC arithmetic). */
export function addDaysMd(dueDay: string, n: number): string {
  const [y, m, d] = dueDay.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d) + n * MS_DAY);
  return `${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

/**
 * Collapses a per-day late-fee breakdown into period rows: an optional grace row,
 * then consecutive runs sharing the same rate. When the accrued fee hit a cap,
 * the trailing zero-fee days become their own "capped" row. Zero-fee days earlier
 * on (a minimum amount front-loads day 1) stay inside their rate run.
 */
export function buildLateFeePeriods(
  dueDay: string,
  grace: number,
  daily: ILateFeeDay[],
  capped = false,
): ILateFeePeriod[] {
  const out: ILateFeePeriod[] = [];
  if (grace > 0) {
    out.push({
      from: addDaysMd(dueDay, 1),
      to: addDaysMd(dueDay, grace),
      days: grace,
      rate: 0,
      fee: 0,
      kind: "grace",
    });
  }
  const sorted = [...daily].sort((a, b) => a.day - b.day);
  // First day of the trailing zero-fee run, counted only when the fee is capped.
  let capStart = Infinity;
  if (capped) {
    for (let i = sorted.length - 1; i >= 0 && !(sorted[i].dailyFee > 0); i--) capStart = sorted[i].day;
  }
  let cur: { first: number; last: number; rate: number; zero: boolean; fee: number; days: number } | null = null;
  const flush = () => {
    if (!cur) return;
    out.push({
      from: addDaysMd(dueDay, grace + cur.first),
      to: addDaysMd(dueDay, grace + cur.last),
      days: cur.days,
      rate: cur.rate,
      fee: cur.fee,
      kind: cur.zero ? "capped" : "charged",
    });
  };
  for (const row of sorted) {
    const zero = row.day >= capStart;
    if (cur && cur.rate === row.rate && cur.zero === zero) {
      cur.last = row.day;
      cur.fee += row.dailyFee;
      cur.days += 1;
    } else {
      flush();
      cur = { first: row.day, last: row.day, rate: row.rate, zero, fee: row.dailyFee, days: 1 };
    }
  }
  flush();
  return out.map((p) => ({ ...p, fee: Math.round(p.fee * 100) / 100 }));
}

/**
 * Rates arrive as DB decimal strings ("0.0500", "30.00"); monthly rates divided by 30
 * have long tails. Show at most 4 decimals without trailing zeros: 0.05%, 30%, 0.0167%.
 */
export function formatPercent(value: number | string): string {
  return `${Number(Number(value).toFixed(4))}%`;
}

/** True when the accrued fee has hit the configured amount or rate cap. */
export function isLateFeeCapped(
  lateFee: number,
  totalAmount: number,
  cfg?: { maxPenaltyAmount?: number | null; maxPenaltyRate?: number | null },
): boolean {
  if (!cfg) return false;
  const r = (n: number) => Math.round(n * 100) / 100;
  const fee = r(lateFee);
  if (cfg.maxPenaltyAmount != null && fee >= r(Number(cfg.maxPenaltyAmount))) return true;
  if (cfg.maxPenaltyRate != null && fee >= r((totalAmount * Number(cfg.maxPenaltyRate)) / 100)) return true;
  return false;
}
